import type {
  CofaClientFilters,
  CofaKindFilter,
  IdentityFilter,
  StatusFilter,
} from '@/shell/services/cofa-registry'

/**
 * CofA Documents saved-search persistence.
 *
 * Ported 2026-04-23 from the existing open-orders pattern at
 * `src/modules/open-orders/utils/saved-searches.ts` (M11 audit Row 11
 * preserve decision — saved searches restored to the cofa-documents
 * surface that lost them at M11 cutover).
 *
 * localStorage-backed, capped at 20 entries. `docKinds` is a
 * `Set<string>` at runtime, which JSON.stringify renders as `{}` if
 * not handled — we (de)serialize through a string[] on the wire to
 * preserve the set contents.
 */

const STORAGE_KEY = 'spi-ops:cofa-documents:saved-searches'

export interface SavedSearch {
  id: string
  label: string
  /** Top-level search string (matches the CofaDocumentsPage search input). */
  search: string
  filters: CofaClientFilters
  createdAt: string
}

interface Serialized {
  id: string
  label: string
  search: string
  filters: {
    status: StatusFilter
    vendorQuery: string
    productQuery: string
    spiOnly: boolean
    excludeBothMissing: boolean
    docKinds: string[]
    cofaKind?: CofaKindFilter
    /** Added 2026-10-08; older saved searches don't have it. */
    identity?: IdentityFilter
    /** Added 2026-10-12. */
    includeUsedUp?: boolean
  }
  createdAt: string
}

function serialize(item: SavedSearch): Serialized {
  return {
    id: item.id,
    label: item.label,
    search: item.search,
    filters: {
      status: item.filters.status,
      vendorQuery: item.filters.vendorQuery,
      productQuery: item.filters.productQuery,
      spiOnly: item.filters.spiOnly,
      excludeBothMissing: item.filters.excludeBothMissing,
      docKinds: [...item.filters.docKinds],
      cofaKind: item.filters.cofaKind,
      identity: item.filters.identity,
      includeUsedUp: item.filters.includeUsedUp,
    },
    createdAt: item.createdAt,
  }
}

function deserialize(raw: Serialized): SavedSearch {
  return {
    id: raw.id,
    label: raw.label,
    search: raw.search,
    filters: {
      status: raw.filters.status,
      vendorQuery: raw.filters.vendorQuery,
      productQuery: raw.filters.productQuery,
      spiOnly: raw.filters.spiOnly,
      excludeBothMissing: raw.filters.excludeBothMissing,
      docKinds: new Set(raw.filters.docKinds),
      cofaKind: raw.filters.cofaKind ?? 'all',
      identity: raw.filters.identity ?? 'all',
      includeUsedUp: raw.filters.includeUsedUp ?? false,
    },
    createdAt: raw.createdAt,
  }
}

export function loadSaved(): SavedSearch[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return []
    return (parsed as Serialized[]).map(deserialize)
  } catch {
    return []
  }
}

function persistSaved(list: SavedSearch[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list.map(serialize)))
  } catch {
    // quota / private mode — non-fatal
  }
}

export function addSaved(
  label: string,
  search: string,
  filters: CofaClientFilters,
): SavedSearch[] {
  const list = loadSaved()
  const item: SavedSearch = {
    id: `s-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    label,
    search,
    filters,
    createdAt: new Date().toISOString(),
  }
  const next = [item, ...list].slice(0, 20)
  persistSaved(next)
  return next
}

export function removeSaved(id: string): SavedSearch[] {
  const next = loadSaved().filter((s) => s.id !== id)
  persistSaved(next)
  return next
}

export function defaultLabelFor(
  search: string,
  filters: CofaClientFilters,
): string {
  const bits: string[] = []
  if (filters.status !== 'all') bits.push(filters.status)
  if (filters.vendorQuery.trim()) bits.push(`vendor=${filters.vendorQuery.trim()}`)
  if (filters.productQuery.trim()) bits.push(`product=${filters.productQuery.trim()}`)
  if (filters.spiOnly) bits.push('SPI only')
  if (filters.excludeBothMissing) bits.push('hide both-missing')
  if (search.trim()) bits.push(`"${search.trim()}"`)
  return bits.length ? bits.join(' · ') : 'Filter ' + new Date().toLocaleDateString()
}
